import { BASE_PATH } from '@trinnvis/dabih-api-client';
import {
  currentUserUuid,
  getClassificationText,
  getOrganization,
  key,
  toGuidelineViewModel,
  writeAccess,
} from 'src/store/selectors';
import type { State } from 'src/store/types.js';
import { LocalDate } from 'src/utilities/local-date.js';
import type { ApiClient } from 'src/store/backend';
import { getPromoDataForEntity } from 'src/store/selectors/promo';
import type { CommonDataEntityView } from '../../content/common-data-entity-view';
import type { GuidelineViewEditItem } from '../../content/guidelines/guideline-view-edit-item';
import type { GuidelineView } from '../../content/guidelines/guideline-view';
import { assertIsDefined } from '../../assertIsDefined';
import { assertEntityFound } from './entity-not-found';
import { toAttachmentItems } from '../to-attachment-items';
import { fetchRevisions } from '../fetch-revisions';
import { pagesAsSelectTagOptions } from '../pages-as-select-tag-options';
import { fetchDraft } from '../fetch-draft';
import { getFeatureStates } from '../../store/selectors/features/getFeatureStates';

export async function buildGuidelineView(
  api: ApiClient,
  uuid: string,
  commonData: CommonDataEntityView,
  state: State,
  currentParent: string,
): Promise<GuidelineView> {
  const organization = getOrganization(state);
  assertIsDefined(organization);

  const availablePages = pagesAsSelectTagOptions(state);
  const item = organization.guidelinesById[uuid];
  assertEntityFound(item, 'guidelines', uuid);

  const entity = toGuidelineViewModel(item);

  return {
    ...commonData,
    promoData: getPromoDataForEntity('guidelines', item, getFeatureStates(state)),
    docsForLinking: [],
    contentLastModified: entity.contentLastModified ?? '',
    contentLastModifiedBy: entity.contentLastModifiedBy ?? '',
    contentLastModifiedWasCreate: entity.contentLastModifiedWasCreate ?? false,
    hasTemplateDelete: entity.hasTemplateDelete ?? false,
    hasTemplateUpdate: entity.hasTemplateUpdate ?? false,
    templateDeclined: entity.templateDeclined ?? '',
    templateDeleted: entity.templateDeleted ?? '',
    templateDeletedMessage: entity.templateDeletedMessage ?? '',
    templateUpdated: entity.templateUpdated ?? '',
    currentUserHasAccess:
      entity.classification === 'NONE' || (entity.accessControl ?? []).includes(currentUserUuid(state) ?? ''),
    currentUserHasWriteAccess: writeAccess(state),
    uuid: uuid,
    isConfirmedEntity: entity.isConfirmedEntity,
    helpContent: entity.helpContent ?? '',
    href: currentParent + '/guidelines/' + uuid,
    parentHref: currentParent,
    pdfHref:
      BASE_PATH + '/organizations/' + organization.organizationId + '/guidelines/' + uuid + '.pdf?key=' + key(state),

    deleted: entity.deleted === true,
    type: 'guidelines',
    name: entity.name ?? '',
    classification: entity.classification ?? 'NONE',
    accessControl: entity.accessControl ?? [],
    classificationText: getClassificationText(
      entity.classification ?? 'NONE',
      entity.accessControl ?? [],
      commonData.accessControlOptions,
    ),
    content: entity.content ?? '',
    revisions: await fetchRevisions(
      api,
      'guidelines',
      uuid,
      organization.organizationId,
      entity.templateDeleted !== undefined && entity.templateDeleted !== '',
      undefined,
      entity.contentLastModified,
    ),
    attachments: toAttachmentItems(api, 'guidelines', entity.uuid, entity.attachments),
    availablePages: availablePages,
    pages: (entity.pages ?? []).map((x) => '' + x),
    today: LocalDate.fromString(state.today),
    deletable: true,
    hasDraft: entity.hasDraft,
    fetchDraft: async () => {
      const command = await fetchDraft(api, 'guidelines', uuid, organization.organizationId);
      return command.draft as GuidelineViewEditItem;
    },
  };
}
